// @flow
import React from 'react';
import styled, { css } from 'react-emotion';
import consts, {
  colors,
  configStructure,
  fonts,
  stepNames,
} from '../assets/scripts/constants';

/**
 * Styles
 */

const Wrapper = styled('div')`
  display: flex;
  flex-basis: 100%;
  flex-wrap: wrap;
  order: 1;
  padding: ${consts.cellEm};

  @media (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (min-width: 860px) {
    // 1% IE11 fix
    flex: 1 1 1%;
    order: unset;
  }
`;

const infoCss = css`
  padding: ${0.5 * consts.cell}em 0 0;
  box-sizing: border-box;
  border: 1px solid ${colors.grayLight};
  border-radius: 0.4em;

  > header {
    padding: ${0.2 * consts.cell}em ${1.1 * consts.cell}em
      ${0.5 * consts.cell}em;
    border-bottom: 1px solid ${colors.grayLight};

    > h2 {
      margin: 0;
      font-family: ${fonts.primary};
      font-size: 2em;
      font-weight: 800;
    }
  }

  > div {
    padding: 0 ${consts.cellEm};
  }
`;

const Options = styled('section')`
  ${infoCss};

  flex: 1 0 0%;

  > div {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    > table {
      width: 100%;
      margin-bottom: 3.2em;
      border-collapse: collapse;

      @media (min-width: 800px) {
        width: 47.5%;
      }

      @media (min-width: 860px) {
        width: 100%;
      }

      @media (min-width: 1024px) {
        width: 47.5%;
      }

      > thead > tr {
        > th {
          padding: ${consts.cell / 2}em;
          text-transform: capitalize;
        }

        > th {
          text-align: left;
          color: ${colors.orange};

          > span {
            font-size: 1.7em;
          }
        }
      }

      > tbody > tr {
        &:nth-of-type(odd) {
          background-color: ${colors.whiteNear};
        }

        > th,
        > td {
          padding: ${consts.cell / 2}em;

          > span {
            display: block;
            font-size: 1.6em;
          }
        }

        > th {
          width: 50%;
          font-weight: 600;
          text-align: left;
          vertical-align: top;
        }

        > td {
          > span + div {
            margin-top: 0.5em;
            font-size: 1.6em;
            color: ${colors.grayDark};
          }
        }
      }
    }
  }
`;

const Delivery = styled('section')`
  ${infoCss};

  order: -1;
  flex: 0 0 auto;
  align-self: flex-start;
  width: 100%;
  margin-bottom: ${consts.cellEm};

  @media (min-width: 1200px) {
    order: 0;
    width: ${11 * consts.cell}em;
    margin-left: ${consts.cellEm};
    margin-bottom: 0;
  }

  > div {
    padding-top: ${consts.cellEm};
    padding-bottom: ${consts.cellEm};

    > span {
      display: block;
      padding-bottom: ${0.3 * consts.cell}em;
      font-size: 2.2em;
      font-weight: 600;
      color: ${colors.orange};

      + a {
        display: block;
        font-size: 1.6em;
        text-decoration: none;
        color: ${colors.grayDark};

        &:hover,
        &:focus {
          text-decoration: underline;
          color: orange;
        }
      }
    }
  }
`;

/**
 * Component: ConfiguratorBottom
 *
 * Shows all configuration selections.
 */
function ConfiguratorBottom(props: { configData: { [string]: Object } }) {
  const { configData } = props;
  const dataValues = Object.keys(configData).map(key => configData[key].value);
  let deliveryTime = +process.env.REACT_APP_DEFAULT_SHIPPING_CLASS;
  deliveryTime = Number.isFinite(deliveryTime) ? deliveryTime : 5;
  return Object.keys(configData).length ? (
    <Wrapper>
      <Options>
        <header>
          <h2>Valitsemasi tuotteen tiedot</h2>
        </header>
        <div>
          {stepNames.map((stepName, stepNumber) => {
            const selections = [];
            configStructure.every(({ filters, slug, step }) => {
              if (step === stepNumber) {
                if (configData[slug]) {
                  const activeFilters = (filters && filters.length
                    ? filters
                    : []
                  ).filter(filter =>
                    filter.slugs.every(
                      termSlug => dataValues.indexOf(termSlug) !== -1
                    )
                  );
                  // If the config structure item has been filtered so that there
                  // is only one option available, hide the preselected value.
                  if (
                    !activeFilters.length ||
                    activeFilters[0].terms.length !== 1
                  ) {
                    selections.push({ ...configData[slug], slug });
                  }
                }
              }
              if (step <= stepNumber) {
                return true;
              }
              return false;
            });
            return selections.length ? (
              <table key={stepName}>
                <thead>
                  <tr>
                    <th colSpan="2">
                      <span>{stepName}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selections.map(item => {
                    deliveryTime =
                      deliveryTime < item.time ? item.time : deliveryTime;
                    return (
                      <tr key={item.slug}>
                        <th>
                          <span>{item.title}</span>
                        </th>
                        <td>
                          <span>
                            {item.label} {/* ({item.value}) */}
                          </span>
                          {!item.custom_color && item.description ? (
                            <div>{item.description}</div>
                          ) : null}
                          {item.custom_color ? (
                            <div>{item.custom_color}</div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null;
          })}
        </div>
      </Options>
      <Delivery>
        <header>
          <h2>Toimitusaika</h2>
        </header>
        <div>
          <span>{deliveryTime === 5 ? '5 arkipäivää' : '8 viikkoa'}</span>
          <a
            href="https://eclisse.muotolevy.fi/toimitusehdot/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Katso toimitusehdot
          </a>
        </div>
      </Delivery>
    </Wrapper>
  ) : (
    <Wrapper />
  );
}

export default ConfiguratorBottom;



// WEBPACK FOOTER //
// ./src/components/ConfiguratorBottom.js